import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Trash2,
  Star,
  Eye,
  EyeOff,
  Camera,
  Aperture,
  Timer,
  Zap,
  Maximize2,
  Users,
} from 'lucide-react';

import { usePhoto, useUpdatePhoto, useDeletePhoto } from '@/api/hooks/usePhotos';
import {
  getPhotoFileUrl,
  getPhotoThumbnailUrl,
} from '@/api/services/photo.service';
import { QualityScoreMeter } from '@/components/gallery/QualityScoreMeter';
import { BestPickBadge } from '@/components/gallery/BestPickBadge';

export function PhotoDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: photo, isLoading, isError } = usePhoto(id ?? '');
  const updateMutation = useUpdatePhoto();
  const deleteMutation = useDeletePhoto();

  const handleToggleStar = useCallback(() => {
    if (!photo) return;
    updateMutation.mutate({
      photoId: photo.id,
      payload: { is_starred: !photo.is_starred },
    });
  }, [photo, updateMutation]);

  const handleToggleHide = useCallback(() => {
    if (!photo) return;
    updateMutation.mutate({
      photoId: photo.id,
      payload: { is_hidden: !photo.is_hidden },
    });
  }, [photo, updateMutation]);

  const handleDelete = useCallback(() => {
    if (!photo) return;
    deleteMutation.mutate(photo.id, {
      onSuccess: () => navigate('/gallery'),
    });
  }, [photo, deleteMutation, navigate]);

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (isError || !photo) {
    return (
      <div className="page-container">
        <div className="flex items-center gap-3">
          <Link
            to="/gallery"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <p className="text-sm text-red-500">{t('status.error')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/gallery"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="page-title">{photo.original_filename}</h1>
            <div className="mt-0.5 flex items-center gap-2">
              {photo.is_group_best && <BestPickBadge />}
              {photo.phase_label && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {photo.phase_label}
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {photo.pipeline_status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleStar}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              photo.is_starred
                ? 'text-yellow-500'
                : 'text-gray-500 hover:text-yellow-500'
            }`}
          >
            <Star
              className="h-4 w-4"
              fill={photo.is_starred ? 'currentColor' : 'none'}
            />
          </button>
          <button
            onClick={handleToggleHide}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400"
          >
            {photo.is_hidden ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </button>
          <a
            href={getPhotoFileUrl(photo.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t('actions.download')}
          </a>
          <button
            onClick={handleDelete}
            className="btn-danger inline-flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {t('actions.delete')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Image */}
        <div className="card lg:col-span-2">
          <div className="overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
            <img
              src={
                photo.thumbnail_large
                  ? getPhotoThumbnailUrl(photo.id, 'large')
                  : getPhotoThumbnailUrl(photo.id, 'medium')
              }
              alt={photo.original_filename}
              className="mx-auto max-h-[600px] w-auto object-contain"
            />
          </div>

          {/* Caption below image */}
          {(photo.caption_en || photo.caption_he) && (
            <div className="mt-4">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('gallery.caption')}
              </h4>
              {photo.caption_en && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {photo.caption_en}
                </p>
              )}
              {photo.caption_he && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" dir="rtl">
                  {photo.caption_he}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quality */}
          <div className="card">
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">
              {t('gallery.quality')}
            </h3>
            <div className="space-y-3">
              <ScoreRow label={t('gallery.overall')} score={photo.quality_score} />
              <ScoreRow label={t('gallery.sharpness')} score={photo.sharpness_score} />
              <ScoreRow label={t('gallery.exposure')} score={photo.exposure_score} />
              <ScoreRow label={t('gallery.blur')} score={photo.blur_score} />
              <ScoreRow label={t('gallery.noise')} score={photo.noise_score} />
              <div className="flex flex-wrap gap-2 pt-1">
                {photo.is_blurry && <FlagPill label={t('gallery.blurry')} color="red" />}
                {photo.is_overexposed && (
                  <FlagPill label={t('gallery.overexposed')} color="yellow" />
                )}
                {photo.is_underexposed && (
                  <FlagPill label={t('gallery.underexposed')} color="blue" />
                )}
              </div>
            </div>
          </div>

          {/* EXIF */}
          <div className="card">
            <h3 className="mb-4 font-semibold text-gray-900 dark:text-gray-100">
              {t('gallery.exif')}
            </h3>
            <div className="space-y-3">
              {photo.camera_make && (
                <ExifRow
                  icon={<Camera className="h-4 w-4" />}
                  label={t('gallery.camera')}
                  value={`${photo.camera_make} ${photo.camera_model_name}`.trim()}
                />
              )}
              {photo.lens_model && (
                <ExifRow
                  icon={<Maximize2 className="h-4 w-4" />}
                  label={t('gallery.lens')}
                  value={photo.lens_model}
                />
              )}
              {photo.focal_length_mm != null && (
                <ExifRow
                  icon={<Maximize2 className="h-4 w-4" />}
                  label={t('gallery.focalLength')}
                  value={`${photo.focal_length_mm}mm`}
                />
              )}
              {photo.aperture != null && (
                <ExifRow
                  icon={<Aperture className="h-4 w-4" />}
                  label={t('gallery.aperture')}
                  value={`f/${photo.aperture}`}
                />
              )}
              {photo.shutter_speed && (
                <ExifRow
                  icon={<Timer className="h-4 w-4" />}
                  label={t('gallery.shutter')}
                  value={photo.shutter_speed}
                />
              )}
              {photo.iso != null && (
                <ExifRow
                  icon={<Zap className="h-4 w-4" />}
                  label="ISO"
                  value={String(photo.iso)}
                />
              )}
              {photo.width != null && photo.height != null && (
                <ExifRow
                  icon={<Maximize2 className="h-4 w-4" />}
                  label={t('gallery.dimensions')}
                  value={`${photo.width} x ${photo.height}`}
                />
              )}
              {photo.taken_at && (
                <ExifRow
                  icon={<Timer className="h-4 w-4" />}
                  label={t('gallery.takenAt')}
                  value={new Date(photo.taken_at).toLocaleString()}
                />
              )}
            </div>
          </div>

          {/* Faces */}
          {photo.face_count > 0 && (
            <div className="card">
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
                {t('gallery.faces')}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Users className="h-4 w-4" />
                <span>{t('gallery.faceCount', { count: photo.face_count })}</span>
              </div>
            </div>
          )}

          {/* File info */}
          <div className="card">
            <h3 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
              {t('gallery.fileInfo')}
            </h3>
            <div className="space-y-1.5 text-xs text-gray-500 dark:text-gray-400">
              <p>
                {t('gallery.size')}: {formatFileSize(photo.file_size)}
              </p>
              <p>
                {t('gallery.type')}: {photo.content_type}
              </p>
              <p className="truncate" title={photo.checksum_sha256}>
                SHA256: {photo.checksum_sha256}
              </p>
              <p>{t('gallery.created')}: {new Date(photo.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function ScoreRow({ label, score }: { label: string; score: number | null }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs text-gray-600 dark:text-gray-400">
        {label}
      </span>
      <QualityScoreMeter score={score} variant="bar" className="flex-1" />
    </div>
  );
}

function ExifRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-gray-400">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-gray-400">
          {label}
        </p>
        <p className="truncate text-xs font-medium text-gray-700 dark:text-gray-300">
          {value}
        </p>
      </div>
    </div>
  );
}

function FlagPill({
  label,
  color,
}: {
  label: string;
  color: 'red' | 'yellow' | 'blue';
}) {
  const colors = {
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${colors[color]}`}>
      {label}
    </span>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
